import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, Search, Grid3x3, UserCog, Trash } from "lucide-react";
import { api, apiError } from "../lib/api";
import { ROLE_LABELS } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function RequestPermissions() {
  const [perms, setPerms] = useState([]);
  const [products, setProducts] = useState([]);
  const [matrix, setMatrix] = useState({ roles: [], request_types: [], users: [], entries: [] });
  const [savingRole, setSavingRole] = useState(null);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [q, setQ] = useState("");
  const [ovUser, setOvUser] = useState("");
  const [ovType, setOvType] = useState("");
  const [ovAllow, setOvAllow] = useState("true");

  const load = async () => {
    try {
      const [p, pr, m] = await Promise.all([api.get("/request-permissions"), api.get("/products"), api.get("/request-matrix")]);
      setPerms(p.data); setProducts(pr.data); setMatrix(m.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filteredProducts = useMemo(() => {
    const s = q.toLowerCase();
    return products.filter((p) => !s || String(p.product_name || "").toLowerCase().includes(s));
  }, [products, q]);

  // ---- matrix helpers ----
  const roleAllowed = (role, type) => {
    const e = matrix.entries.find((x) => x.from_type === "role" && x.from_key === role && x.request_type === type);
    return e ? !!e.allowed : false;
  };
  const setRoleAllowed = (role, type, val) => setMatrix((prev) => {
    const others = prev.entries.filter((x) => !(x.from_type === "role" && x.from_key === role && x.request_type === type));
    return { ...prev, entries: [...others, { from_type: "role", from_key: role, request_type: type, to_department: "store", allowed: val }] };
  });
  const userEntries = matrix.entries.filter((x) => x.from_type === "user");
  const addUserOverride = () => {
    if (!ovUser || !ovType) { toast.error("Pick a user and request type"); return; }
    setMatrix((prev) => {
      const others = prev.entries.filter((x) => !(x.from_type === "user" && x.from_key === ovUser && x.request_type === ovType));
      return { ...prev, entries: [...others, { from_type: "user", from_key: ovUser, request_type: ovType, to_department: "store", allowed: ovAllow === "true" }] };
    });
    setOvUser(""); setOvType("");
  };
  const removeUserOverride = (uid, type) => setMatrix((prev) => ({ ...prev, entries: prev.entries.filter((x) => !(x.from_type === "user" && x.from_key === uid && x.request_type === type)) }));
  const userName = (uid) => { const u = matrix.users.find((x) => x.id === uid); return u ? (u.employee_name || u.name) : uid; };
  const typeLabel = (k) => (matrix.request_types.find((t) => t.key === k) || {}).label || k;

  const saveMatrix = async () => {
    setSavingMatrix(true);
    try { await api.put("/request-matrix", { entries: matrix.entries }); toast.success("Request matrix saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSavingMatrix(false);
  };

  // ---- product perms ----
  const setRole = (role, patch) => setPerms((prev) => prev.map((r) => (r.role === role ? { ...r, ...patch } : r)));
  const toggleProduct = (role, pid) => setPerms((prev) => prev.map((r) => {
    if (r.role !== role) return r;
    const set = new Set(r.allowed_products || []);
    if (set.has(pid)) set.delete(pid); else set.add(pid);
    return { ...r, allowed_products: [...set] };
  }));
  const saveRole = async (r) => {
    setSavingRole(r.role);
    try { await api.put(`/request-permissions/${r.role}`, { can_request: r.can_request, allowed_products: r.allowed_products || [], can_store_manage: r.can_store_manage }); toast.success(`${ROLE_LABELS[r.role] || r.role} saved`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSavingRole(null);
  };

  return (
    <div data-testid="request-permissions-page">
      <div className="mb-6"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head flex items-center gap-2"><ShieldCheck className="w-6 h-6" /> Request Permissions</h1>
        <p className="text-sm text-slate-500 mt-0.5">Control who can send which request to whom. Nothing is enabled by default — the admin must explicitly grant access.</p></div>

      {/* Permission Matrix */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6" data-testid="request-matrix">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-slate-800 flex items-center gap-2"><Grid3x3 className="w-4 h-4" /> Permission Matrix — From → Request Type → To</div>
          <Button data-testid="save-matrix" size="sm" onClick={saveMatrix} disabled={savingMatrix} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Save className="w-4 h-4" /> Save Matrix</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 text-xs border-b border-slate-100">
              <th className="py-2 pr-4">From Department</th>
              {matrix.request_types.map((t) => <th key={t.key} className="py-2 px-3 text-center">{t.label} → Store</th>)}
            </tr></thead>
            <tbody>
              {matrix.roles.map((role) => (
                <tr key={role} className="border-b border-slate-50" data-testid={`matrix-row-${role}`}>
                  <td className="py-2 pr-4 font-medium text-slate-700">{ROLE_LABELS[role] || role}</td>
                  {matrix.request_types.map((t) => (
                    <td key={t.key} className="py-2 px-3 text-center">
                      <Checkbox data-testid={`matrix-${role}-${t.key}`} checked={roleAllowed(role, t.key)} onCheckedChange={(v) => setRoleAllowed(role, t.key, !!v)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* User-level overrides */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="font-semibold text-slate-800 flex items-center gap-2 mb-2"><UserCog className="w-4 h-4" /> User-Level Overrides</div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Select value={ovUser} onValueChange={setOvUser}><SelectTrigger data-testid="ov-user" className="bg-white w-56"><SelectValue placeholder="Select user" /></SelectTrigger>
              <SelectContent>{matrix.users.map((u) => <SelectItem key={u.id} value={u.id}>{(u.employee_name || u.name)} ({ROLE_LABELS[u.role] || u.role})</SelectItem>)}</SelectContent></Select>
            <Select value={ovType} onValueChange={setOvType}><SelectTrigger data-testid="ov-type" className="bg-white w-48"><SelectValue placeholder="Request type" /></SelectTrigger>
              <SelectContent>{matrix.request_types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select>
            <Select value={ovAllow} onValueChange={setOvAllow}><SelectTrigger data-testid="ov-allow" className="bg-white w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">Allow</SelectItem><SelectItem value="false">Deny</SelectItem></SelectContent></Select>
            <Button data-testid="ov-add" size="sm" variant="outline" className="rounded-sm" onClick={addUserOverride}>Add Override</Button>
          </div>
          <div className="space-y-1">
            {userEntries.length === 0 ? <div className="text-xs text-slate-400">No user-level overrides. Users inherit their department's matrix permissions.</div>
              : userEntries.map((e) => (
                <div key={`${e.from_key}_${e.request_type}`} className="flex items-center justify-between text-sm bg-slate-50 rounded-sm px-3 py-1.5" data-testid={`ov-row-${e.from_key}-${e.request_type}`}>
                  <span>{userName(e.from_key)} · {typeLabel(e.request_type)} → Store · <span className={e.allowed ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{e.allowed ? "Allowed" : "Denied"}</span></span>
                  <button onClick={() => removeUserOverride(e.from_key, e.request_type)} className="text-slate-400 hover:text-red-600"><Trash className="w-3.5 h-3.5" /></button>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Product-level material request permissions */}
      <div className="mb-3 flex items-center justify-between">
        <div className="font-semibold text-slate-800">Material Request — Allowed Products (per department)</div>
        <div className="relative max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="rp-product-search" placeholder="Filter products..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
        </div>
      </div>
      <div className="space-y-4">
        {perms.map((r) => (
          <div key={r.role} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`rp-role-${r.role}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="font-semibold text-slate-800">{ROLE_LABELS[r.role] || r.role}</div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><Checkbox data-testid={`rp-store-${r.role}`} checked={!!r.can_store_manage} onCheckedChange={(v) => setRole(r.role, { can_store_manage: !!v })} /> Store manager (accept/issue)</label>
                <Button data-testid={`rp-save-${r.role}`} size="sm" onClick={() => saveRole(r)} disabled={savingRole === r.role} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Save className="w-4 h-4" /> Save Products</Button>
              </div>
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Allowed Products ({(r.allowed_products || []).length} selected)</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-56 overflow-y-auto border border-slate-100 rounded-sm p-2">
              {filteredProducts.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer py-0.5">
                  <Checkbox data-testid={`rp-prod-${r.role}-${p.id}`} checked={(r.allowed_products || []).includes(p.id)} onCheckedChange={() => toggleProduct(r.role, p.id)} />
                  <span className="truncate">{p.product_name}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
